import CachedIcon from "@mui/icons-material/Cached";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SwapHorizOutlined from "@mui/icons-material/SwapHorizOutlined";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import {
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Tooltip,
} from "@mui/material";
import { FeatureFlagOverride } from "encamp-shared/src/generated-graphql/oak-resolver-types";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { range } from "lodash";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import { Row } from ".";
import { SetCollectionModeModal } from "../Collection/SetCollectionModeModal";
import { QueueExtractionsModal } from "../Extractions/QueueExtractionsModal";
import { BulkSetChemicalFlagModal } from "./BulkSetChemicalFlagModal";
import { BulkSetWasteFlagModal } from "./BulkSetWasteFlagModal";
import { CopyFacilityInventoriesModal } from "./CopyFacilityInventoriesModal";

const buttonId = "facilities-bulk-actions-button";
const menuId = "facilities-bulk-actions-menu";

type BulkActionMenuProps = {
  selectedRows: Row[];
};

type DialogState =
  | { action: "setCollectionMode" }
  | { action: "copyInventory" }
  | { action: "setWasteFlag"; featureFlagOverride: FeatureFlagOverride }
  | { action: "setChemicalFlag"; featureFlagOverride: FeatureFlagOverride }
  | { action: "queueExtractions"; year: number }
  | null;

const bulkActionContext = createContext<{
  selectedRows: Row[];
  dialogState: DialogState;
  setDialogState: Dispatch<SetStateAction<DialogState>>;
  onClose: () => void;
}>({
  selectedRows: [],
  dialogState: null,
  setDialogState: () => {},
  onClose: () => {},
});

export function useBulkActionContext() {
  return useContext(bulkActionContext);
}

const FacilitiesBulkActionsMenu = ({ selectedRows }: BulkActionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const onClose = () => {
    setAnchorEl(null);
    setDialogState(null);
  };

  return (
    <bulkActionContext.Provider
      value={{ selectedRows, onClose, dialogState, setDialogState }}
    >
      <Button
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        disabled={!selectedRows.length}
        variant="contained"
        onClick={handleClick}
        endIcon={<ExpandMore />}
        sx={{ whiteSpace: "nowrap" }}
      >
        Bulk Actions
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        MenuListProps={{ "aria-labelledby": buttonId }}
      >
        <MenuList>
          <SetCollectionModeMenuItem />
          <CopyInventoryMenuItem />
          <Divider />
          <WasteMenuItems />
          <ChemicalMenuItems />
          <Divider />
          <QueueExtractionsMenuItems />
        </MenuList>
      </Menu>
      <SetCollectionModeModal />
      <CopyFacilityInventoriesModal />
      <QueueExtractionsModal />
      <BulkSetWasteFlagModal />
      <BulkSetChemicalFlagModal />
    </bulkActionContext.Provider>
  );
};

export { FacilitiesBulkActionsMenu };

function SetCollectionModeMenuItem() {
  const { selectedRows, setDialogState } = useBulkActionContext();
  return (
    <MenuItem
      disabled={selectedRows.length === 0}
      onClick={() => setDialogState({ action: "setCollectionMode" })}
    >
      <ListItemIcon>
        <SwapHorizOutlined />
      </ListItemIcon>
      <ListItemText>Set Collection Mode</ListItemText>
    </MenuItem>
  );
}

function WasteMenuItems() {
  const { selectedRows, setDialogState } = useBulkActionContext();
  const { featureFlags } = useFeatureFlags();
  const isWasteEnabled = featureFlags?.["module-waste"] ?? false;
  const tooltip = isWasteEnabled
    ? ""
    : "This tenant does not have Waste enabled";

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <MenuItem
            disabled={selectedRows.length === 0}
            onClick={() =>
              setDialogState({
                action: "setWasteFlag",
                featureFlagOverride: FeatureFlagOverride.Enabled,
              })
            }
          >
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText>Turn on Waste</ListItemText>
          </MenuItem>
        </span>
      </Tooltip>
      <Tooltip title={tooltip}>
        <span>
          <MenuItem
            disabled={selectedRows.length === 0}
            onClick={() =>
              setDialogState({
                action: "setWasteFlag",
                featureFlagOverride: FeatureFlagOverride.Disabled,
              })
            }
          >
            <ListItemIcon>
              <DeleteOutlineIcon />
            </ListItemIcon>
            <ListItemText>Turn off Waste</ListItemText>
          </MenuItem>
        </span>
      </Tooltip>
    </>
  );
}

function ChemicalMenuItems() {
  const { selectedRows, setDialogState } = useBulkActionContext();
  const { featureFlags } = useFeatureFlags();
  const isChemicalEnabled = featureFlags?.["module-tierii"] ?? false;
  const tooltip = isChemicalEnabled
    ? ""
    : "This tenant does not have Chemicals enabled";

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <MenuItem
            disabled={selectedRows.length === 0}
            onClick={() =>
              setDialogState({
                action: "setChemicalFlag",
                featureFlagOverride: FeatureFlagOverride.Enabled,
              })
            }
          >
            <ListItemIcon>
              <WarehouseIcon />
            </ListItemIcon>
            <ListItemText>Turn on Chemicals</ListItemText>
          </MenuItem>
        </span>
      </Tooltip>
      <Tooltip title={tooltip}>
        <span>
          <MenuItem
            disabled={selectedRows.length === 0}
            onClick={() =>
              setDialogState({
                action: "setChemicalFlag",
                featureFlagOverride: FeatureFlagOverride.Disabled,
              })
            }
          >
            <ListItemIcon>
              <WarehouseOutlinedIcon />
            </ListItemIcon>
            <ListItemText>Turn off Chemicals</ListItemText>
          </MenuItem>
        </span>
      </Tooltip>
    </>
  );
}

function CopyInventoryMenuItem() {
  const currentYear = new Date().getFullYear();
  const { selectedRows, setDialogState } = useBulkActionContext();

  return (
    <MenuItem
      disabled={selectedRows.length === 0}
      onClick={() => setDialogState({ action: "copyInventory" })}
    >
      <ListItemIcon>
        <CopyAllIcon />
      </ListItemIcon>
      <ListItemText>Copy Last Year's Inventory to {currentYear}</ListItemText>
    </MenuItem>
  );
}

export function QueueExtractionsMenuItems() {
  const currentYear = new Date().getFullYear();
  const { selectedRows, setDialogState } = useBulkActionContext();
  return (
    <>
      {range(currentYear, currentYear - 4).map((reportingYear) => (
        <MenuItem
          key={reportingYear}
          disabled={selectedRows.length === 0}
          onClick={() =>
            setDialogState({ action: "queueExtractions", year: reportingYear })
          }
        >
          <ListItemIcon>
            <CachedIcon />
          </ListItemIcon>
          <ListItemText>Queue Extractions for {reportingYear}</ListItemText>
        </MenuItem>
      ))}
    </>
  );
}
